import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  CheckCircle, 
  Users, 
  BarChart3, 
  FileSpreadsheet, 
  Bell, 
  Shield,
  ArrowRight,
  Sparkles,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  Phone
} from "lucide-react";
import { toast } from "sonner";

export const LandingPage = () => {
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [authMode, setAuthMode] = useState("login"); // "login" or "register"
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        // If authenticated, redirect to appropriate dashboard
        if (response.data.role === "admin") {
          navigate("/admin", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } catch (error) {
        // Not authenticated, stay on landing page
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (authMode === "register") {
        // Register
        if (!formData.name.trim()) {
          toast.error("Please enter your name");
          setIsLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          toast.error("Password must be at least 6 characters");
          setIsLoading(false);
          return;
        }

        const response = await axios.post(
          `${API}/auth/register`,
          {
            email: formData.email,
            password: formData.password,
            name: formData.name
          },
          { withCredentials: true }
        );

        const user = response.data;
        toast.success("Account created successfully!");
        
        // Redirect based on role
        if (user.role === "admin") {
          navigate("/admin", { replace: true, state: { user } });
        } else {
          navigate("/dashboard", { replace: true, state: { user } });
        }
      } else {
        // Login
        const response = await axios.post(
          `${API}/auth/login`,
          {
            email: formData.email,
            password: formData.password
          },
          { withCredentials: true }
        );

        const user = response.data;
        toast.success("Welcome back!");
        
        // Redirect based on role
        if (user.role === "admin") {
          navigate("/admin", { replace: true, state: { user } });
        } else {
          navigate("/dashboard", { replace: true, state: { user } });
        }
      }
    } catch (error) {
      console.error("Auth error:", error);
      const message = error.response?.data?.detail || "Authentication failed";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: FileSpreadsheet,
      title: "Excel Import",
      description: "Import customer lists and schedules directly from Excel files"
    },
    {
      icon: Phone,
      title: "QA Calls",
      description: "Track quality assurance follow-up calls with customers"
    },
    {
      icon: BarChart3,
      title: "Performance Tracking",
      description: "Monitor technician ratings and daily call metrics"
    },
    {
      icon: Bell,
      title: "Notifications",
      description: "Alert technicians about customer feedback automatically"
    },
    {
      icon: Shield,
      title: "Role-based Access",
      description: "Admin, QA Agent, and Employee roles with appropriate permissions"
    },
    {
      icon: CheckCircle,
      title: "Task Management",
      description: "Assign tasks, track progress, and manage your team"
    }
  ];

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-[#0066CC] flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-heading font-bold text-foreground">Lexia Communications</span>
            </div>
            <Button 
              onClick={() => document.getElementById('auth-section').scrollIntoView({ behavior: 'smooth' })}
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm rounded-md px-4 py-2 font-medium transition-all active:scale-95"
              data-testid="navbar-login-btn"
            >
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0066CC]/10 text-[#0066CC] text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                <span>Quality Assurance & Task Management</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground tracking-tight mb-6 leading-tight">
                Lexia <br />
                <span className="text-[#0066CC]">Communications</span>
              </h1>
              
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
                A powerful portal for QA follow-up calls and task management. Import customer lists, 
                track technician performance, and manage your team efficiently.
              </p>

              <div className="flex items-center gap-6 mt-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Free to start</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>No credit card</span>
                </div>
              </div>
            </motion.div>

            {/* Right - Auth Form */}
            <motion.div
              id="auth-section"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-border/50 p-8 max-w-md mx-auto">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                    {authMode === "login" ? "Welcome Back" : "Create Account"}
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    {authMode === "login" 
                      ? "Sign in to your account to continue" 
                      : "Register to get started with Lexia Communications"}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {authMode === "register" && (
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="pl-10"
                          data-testid="register-name-input"
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="pl-10"
                        data-testid="auth-email-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={authMode === "register" ? "Min 6 characters" : "Enter password"}
                        value={formData.password}
                        onChange={handleInputChange}
                        className="pl-10 pr-10"
                        data-testid="auth-password-input"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md rounded-lg py-2.5 font-semibold transition-all"
                    data-testid="auth-submit-btn"
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                        <span>Please wait...</span>
                      </div>
                    ) : (
                      <span>{authMode === "login" ? "Sign In" : "Create Account"}</span>
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    {authMode === "login" ? "Don't have an account?" : "Already have an account?"}
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode(authMode === "login" ? "register" : "login");
                        setFormData({ email: "", password: "", name: "" });
                      }}
                      className="ml-1 text-primary font-medium hover:underline"
                      data-testid="auth-toggle-mode"
                    >
                      {authMode === "login" ? "Sign up" : "Sign in"}
                    </button>
                  </p>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> First user to register becomes the Admin.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-semibold text-foreground tracking-tight mb-4">
              Everything you need for QA & Task Management
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful features designed for efficient quality assurance and team collaboration
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 border border-border shadow-card hover:shadow-card-hover transition-shadow duration-300"
              >
                <div className="w-12 h-12 rounded-lg bg-[#0066CC]/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#0066CC]" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-semibold text-foreground tracking-tight mb-4">
              Ready to improve your QA process?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join Lexia Communications to manage QA calls and tasks efficiently.
            </p>
            <Button 
              onClick={() => document.getElementById('auth-section').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-[#0066CC] text-white hover:bg-[#0055AA] shadow-md rounded-lg px-8 py-3 font-semibold transition-all active:scale-95"
              data-testid="cta-get-started-btn"
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#0066CC] flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-foreground">Lexia Communications</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2025 Lexia Communications. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
